// src/api.ts
// Legacy fetch wrapper (~150 endpoint functions), converted to TypeScript as
// part of the full frontend TS migration (see CLAUDE.md's now-completed
// "Frontend TypeScript Migration State"). This module's role hasn't
// changed: it's the thin req()-based client every pre-contract endpoint
// still goes through, kept alive because the last handful of screens
// (formerly VisitWorkflow.jsx and its stage children) relied on it.
//
// Response shapes here are still effectively untyped — these are raw
// Postgres rows off `routes/*.js`, never modeled 1:1 in a shared type the
// way `lib/api/client.ts`'s contract-first endpoints are. Rather than fake
// precision with invented interfaces per row shape, callers get `Promise<any>`
// from `req()` (explicit, not implicit — `noImplicitAny` is satisfied by the
// generic's default) and are expected to narrow locally, same as before this
// file was TypeScript. Parameters *are* precisely typed throughout: ids,
// query params and request bodies are exactly as strict as the endpoint's
// actual contract, since that costs nothing to get right and catches a
// genuine class of bug (wrong param order, a forgotten field) at compile time.
import { loadConfig, getActiveModel } from './aiConfig';
// Same shape as lib/api/session.ts's typed session helpers — deliberately
// reused rather than re-declared, since this module reads/writes the exact
// same localStorage keys (see that file's own header comment: "one app, one
// session"). A screen migrated to the typed client and a screen still on
// this legacy helper must agree on what a logged-in user looks like.
import type { SessionUser } from './lib/api/session';

const BASE = '/api';
const TOKEN_KEY  = 'hms_auth_token';
const USER_KEY   = 'hms_auth_user';
const BRANCH_KEY = 'hms_active_branch_id';

// The self-service kiosk (?kiosk=1&token=...) has no staff login at all, so
// it can't carry an Authorization header — it authenticates as "the kiosk
// terminal" via this shared device token instead, read once from its own
// URL. Every other page's URL never has ?token=, so this is a no-op there.
const KIOSK_TOKEN = new URLSearchParams(window.location.search).get('token');

export type AuthUser = SessionUser;

export function getAuthToken(): string | null { return localStorage.getItem(TOKEN_KEY); }
export function getAuthUser(): AuthUser | null { try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; } }
export function setAuthSession(token: string, user: AuthUser): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  if (user?.active_branch?.id) setActiveBranchId(user.active_branch.id);
}
export function clearAuthSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(BRANCH_KEY);
}

// The branch a staff member is currently acting in — sent as X-Branch-Id on
// every authenticated request (see middleware/auth.js's requireAuth, which
// re-validates it against the user's grants on the server; this is only a
// preference, never a trust boundary on its own). Kiosk requests never send
// this — a kiosk's branch comes from its device token instead.
export function getActiveBranchId(): string | null { return localStorage.getItem(BRANCH_KEY) || null; }
export function setActiveBranchId(branchId: string | number): void { localStorage.setItem(BRANCH_KEY, String(branchId)); }

function branchHeaders(): Record<string, string> {
  const branchId = getActiveBranchId();
  return branchId ? { 'X-Branch-Id': branchId } : {};
}

/** Every plain-object query-param bag in this file, converted to a search
 * string. Values are stringified (numbers/booleans included, since several
 * call sites pass a numeric id or a boolean flag); `undefined`/`null` entries
 * are dropped rather than serialized as the literal string "undefined". */
export type QueryParams = Record<string, string | number | boolean | undefined | null>;

function qs(params: QueryParams = {}): string {
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) out.set(k, String(v));
  }
  return out.toString();
}

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

// For components that call fetch() directly (file uploads, multi-step stage
// flows, etc.) instead of the req() helper below — still needs the same
// Authorization headers merged in, since most endpoints now require auth.
// No x-user header: attribution is server-side from the verified JWT, and the
// server strips x-user on arrival (KMC #26) — a client-typed name is not an identity.
export function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getAuthToken();
  // options.headers is typed HeadersInit (Headers | string[][] | Record<string,string>)
  // by lib.dom, but every call site in this codebase passes a plain object literal.
  const headers: Record<string, string> = { ...(options.headers as Record<string, string> | undefined) };
  if (token) { headers['Authorization'] = `Bearer ${token}`; Object.assign(headers, branchHeaders()); }
  else if (KIOSK_TOKEN) headers['X-Kiosk-Token'] = KIOSK_TOKEN;
  return fetch(url, { ...options, headers });
}

async function req<T = any>(method: HttpMethod, path: string, body?: unknown): Promise<T> {
  const token = getAuthToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) { headers['Authorization'] = `Bearer ${token}`; Object.assign(headers, branchHeaders()); }
  else if (KIOSK_TOKEN) headers['X-Kiosk-Token'] = KIOSK_TOKEN;

  const res = await fetch(`${BASE}${path}`, {
    method, headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    // A 401 while we thought we had a session means it expired/was revoked —
    // force back to the login screen. A 401 from /auth/login itself is just
    // "wrong password" and should be shown inline, not treated as a logout.
    if (res.status === 401 && token && path !== '/auth/login') {
      clearAuthSession();
      window.location.reload();
    }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── Auth ───────────────────────────────────────────────────────
export const login = (username: string, password: string) => req('POST', '/auth/login', { username, password });
export const getMe = () => req('GET', '/auth/me');
export const changePassword = (current_password: string, new_password: string) => req('POST', '/auth/change-password', { current_password, new_password });

// ── Users (admin) ────────────────────────────────────────────────
export const getAllUsers   = ()                              => req('GET',  '/users');
export const getUsersByRole = (role: string)                 => req('GET',  `/users?role=${encodeURIComponent(role)}`);
export const createUser    = (data: Record<string, unknown>) => req('POST', '/users', data);
export const updateUser    = (id: number, data: Record<string, unknown>) => req('PATCH', `/users/${id}`, data);
export const deactivateUser = (id: number)                   => req('DELETE', `/users/${id}`);
export const getUserBranches = (id: number)                       => req('GET', `/users/${id}/branches`);
export const setUserBranches = (id: number, data: Record<string, unknown>) => req('PUT', `/users/${id}/branches`, data);

// ── Roles (admin) ────────────────────────────────────────────────
export const getAllRoles   = ()                                   => req('GET',  '/roles');
export const createRole    = (data: Record<string, unknown>)      => req('POST', '/roles', data);
export const updateRole    = (id: number, data: Record<string, unknown>) => req('PATCH', `/roles/${id}`, data);
export const deleteRole    = (id: number)                         => req('DELETE', `/roles/${id}`);

// ── Ailment templates (admin manages; nurse reads during Pre-Consult) ────────
export const getAilmentTemplates  = ()                            => req('GET',  '/ailment-templates');
export const createAilmentTemplate = (data: Record<string, unknown>) => req('POST', '/ailment-templates', data);
export const updateAilmentTemplate = (id: number, data: Record<string, unknown>) => req('PATCH', `/ailment-templates/${id}`, data);
export const deactivateAilmentTemplate = (id: number)             => req('DELETE', `/ailment-templates/${id}`);
export const getAssessmentSummary = (templateId: number, answers: Record<string, unknown>) => req('POST', `/ailment-templates/${templateId}/summary`, { answers });
/** Prior values of this template's numeric fields for one patient, oldest
 * first — backs the trend beside a numeric Pre-Consult field. Tenant-wide
 * (clinical history, reached by UHID); see routes/patient-history.js. */
export const getAssessmentTrend = (uhid: string, templateId: number, fields: string[], limit = 6) =>
  req('GET', `/patients/${uhid}/assessment-trend?template_id=${templateId}&fields=${encodeURIComponent(fields.join(','))}&limit=${limit}`);
export const resetAilmentTemplateToDefault = (id: number)         => req('POST', `/ailment-templates/${id}/reset-to-default`);
export const approveAilmentTemplate = (id: number)                => req('POST', `/ailment-templates/${id}/approve`);
export const rejectAilmentTemplate  = (id: number, notes?: string) => req('POST', `/ailment-templates/${id}/reject`, { notes });

// ── AI Assistant Role profiles (admin manages; applied at runtime in Consultation) ────
export const getAIAssistantProfiles   = ()                        => req('GET',  '/ai-assistant-profiles');
export const getAIAssistantProfile    = (id: number)              => req('GET',  `/ai-assistant-profiles/${id}`);
export const createAIAssistantProfile = (data: Record<string, unknown>) => req('POST', '/ai-assistant-profiles', data);
export const updateAIAssistantProfile = (id: number, data: Record<string, unknown>) => req('PATCH', `/ai-assistant-profiles/${id}`, data);
export const requestActivateAIAssistantProfile = (id: number)     => req('POST', `/ai-assistant-profiles/${id}/request-activate`);
export const approveAIAssistantProfile = (id: number)             => req('POST', `/ai-assistant-profiles/${id}/approve`);
export const rejectAIAssistantProfile  = (id: number, notes?: string) => req('POST', `/ai-assistant-profiles/${id}/reject`, { notes });

// ── Patient merge requests (any staff requests; Admin approves/rejects) ──────
export const getMergeRequests   = ()                              => req('GET',  '/patient-merge-requests');
export const createMergeRequest = (data: Record<string, unknown>) => req('POST', '/patient-merge-requests', data);
export const approveMergeRequest = (id: number)                   => req('POST', `/patient-merge-requests/${id}/approve`);
export const rejectMergeRequest  = (id: number, notes?: string)   => req('POST', `/patient-merge-requests/${id}/reject`, { notes });

// ── IP module (wards/beds/admissions) ─────────────────────────────────────
export const getWards         = ()                                => req('GET',  '/ip/wards');
export const createWard       = (data: Record<string, unknown>)   => req('POST', '/ip/wards', data);
export const updateWard       = (id: number, data: Record<string, unknown>) => req('PATCH', `/ip/wards/${id}`, data);
export const addBed           = (wardId: number, data: Record<string, unknown>) => req('POST', `/ip/wards/${wardId}/beds`, data);
export const updateBed        = (id: number, data: Record<string, unknown>) => req('PATCH', `/ip/beds/${id}`, data);
export const getAdmissions    = (status?: string)                 => req('GET',  `/ip/admissions${status ? `?status=${status}` : ''}`);
export const getActiveAdmissionForPatient = (patientId: number)   => req<any[]>('GET', `/ip/admissions?status=admitted&patient_id=${patientId}`).then(rows => rows[0] || null);
export const getAdmission     = (id: number)                      => req('GET',  `/ip/admissions/${id}`);
export const createAdmission  = (data: Record<string, unknown>)   => req('POST', '/ip/admissions', data);
export const addIPCharge      = (admissionId: number, data: Record<string, unknown>) => req('POST', `/ip/admissions/${admissionId}/charges`, data);
export const removeIPCharge   = (admissionId: number, chargeId: number) => req('DELETE', `/ip/admissions/${admissionId}/charges/${chargeId}`);
export const dischargeAdmission = (admissionId: number, data: Record<string, unknown>) => req('POST', `/ip/admissions/${admissionId}/discharge`, data);
// Insurance Claims Phase 2.8 — the live, unsaved running-bill statement
// (utils/finalBill.js#buildStatement), head-wise totals. Recomputed on every
// call; never cached beyond this request.
export const getRunningBill   = (admissionId: number)              => req('GET',  `/ip/admissions/${admissionId}/running-bill`);
// Tenant-wide doctor roster, for a picker (Ward admission's "Admitting doctor") —
// same endpoint features/appointments/api.ts's fetchDoctors uses.
export const getDoctors       = ()                                => req('GET',  '/users?role=Doctor');

// ── Patients ───────────────────────────────────────────────────
export const createPatient   = (data: Record<string, unknown>)    => req('POST', '/patients/create', data);
export const checkDuplicatePatient = (data: Record<string, unknown>) => req('POST', '/patients/check-duplicate', data);
export const saveVitals      = (data: Record<string, unknown>)    => req('POST', '/patients/vitals/save', data);
export const searchPatients  = (q: string)                        => req('GET',  `/patients/search?q=${encodeURIComponent(q)}`);
export const getAllPatients  = ()                                 => req('GET',  '/patients');
// Paged listing for the Patient Records landing view — `?page=` opts into
// the envelope shape ({ data, page, limit, total, has_more }) per
// apiResponse.js, unlike getAllPatients() above which stays the legacy bare
// array (still used for the top search bar's full-list client-side filter).
export const getPatientsPage = (page: number, limit = 50)         => req('GET', `/patients?page=${page}&limit=${limit}`);
export const getTodayActivityPatients = ()                        => req('GET',  '/patients/today-activity');
export const getPatient      = (uhid: string)                     => req('GET',  `/patients/${uhid}`);
export const updatePatient   = (uhid: string, data: Record<string, unknown>) => req('PATCH', `/patients/${uhid}`, data);
// getPatientSummary/generatePatientSummary moved to features/patients/api.ts
// (2026-09-17 redesign Phase 3) — that route's AI_CONSENT_REQUIRED governance
// code needs to survive the client's error handling, which this file's req()
// throws away (see that file's own comment).
export const getPatientTimeline = (uhid: string)                  => req('GET',  `/patients/${uhid}/timeline`);
export const generatePatientTimeline = (uhid: string, provider: string, model: string) => req('POST', `/patients/${uhid}/timeline/generate`, { provider, model });

// Tenant AI processing policy singleton — GET is open to any authenticated
// staff member (routes/ai-settings.js), so a screen can check up front
// whether it's worth calling AI at all. See ConsultationChat.tsx's
// aiEnabled — whether the initial consultation note is AI-drafted or
// starts as the deterministic captured-data skeleton.
export const getAiSettings = (): Promise<{ ai_processing_enabled: boolean; ai_consent_required: boolean; ai_suggestions_enabled?: boolean; default_ai_provider?: string | null; default_voice_provider?: string; structured_clinical_draft?: boolean; consultation_audio_retention_days?: number }> =>
  req('GET', '/ai-settings');

// Patient consent (patient_consents) — see routes/patient-consent.js.
//
// SUPERSEDED by features/consent/api.ts, which is typed, carries the consent
// wording/version/expiry these two never knew about, and is what every screen
// now uses (the Registration consent step, PatientRecords). Left in place
// because this module is the legacy client for screens that have not been
// migrated, and removing an export from it has bitten this file before —
// prefer features/consent for anything new.
export const getPatientConsent = (uhid: string)                   => req('GET',  `/patients/${uhid}/consent`);
export const setPatientConsent = (uhid: string, type: string, granted: boolean, notes?: string) =>
  req('PUT', `/patients/${uhid}/consent/${type}`, { granted, notes });

// ── Global search ──────────────────────────────────────────────
export const globalSearch    = (q: string)                        => req('GET',  `/search?q=${encodeURIComponent(q)}`);

// ── Structured patient history (diagnoses / procedures / medications) ──
// Editable "problem list", distinct from the read-only AI timeline above —
// seeded from it (source='ai') but directly maintained by staff thereafter.
interface HistoryEndpoints {
  list:   (uhid: string) => Promise<any>;
  create: (uhid: string, data: Record<string, unknown>) => Promise<any>;
  update: (uhid: string, id: number, data: Record<string, unknown>) => Promise<any>;
  remove: (uhid: string, id: number) => Promise<any>;
}
function historyEndpoints(kind: string): HistoryEndpoints {
  return {
    list:   (uhid)          => req('GET',    `/patients/${uhid}/${kind}`),
    create: (uhid, data)    => req('POST',   `/patients/${uhid}/${kind}`, data),
    update: (uhid, id, data)=> req('PATCH',  `/patients/${uhid}/${kind}/${id}`, data),
    remove: (uhid, id)      => req('DELETE', `/patients/${uhid}/${kind}/${id}`),
  };
}
export const patientDiagnoses   = historyEndpoints('diagnoses');
export const patientProcedures  = historyEndpoints('procedures');
export const patientMedications = historyEndpoints('medications');

// ── Allergies ────────────────────────────────────────────────────
// Same /:uhid/<kind> shape as the history endpoints above, but not built via
// historyEndpoints(): the list read takes a flag, and remove() retracts
// (verification_status='entered-in-error') rather than deleting — an allergy
// that was acted on has to stay visible as having existed.
//
// "No known allergies" is a row, not an empty list: create() it with
// substance:'No known allergies'. Empty means nobody asked, which is not the
// same thing and is not safe to prescribe against.
export const patientAllergies = {
  list:   (uhid: string, opts: { includeInactive?: boolean } = {}) =>
            req('GET', `/patients/${uhid}/allergies${opts.includeInactive ? '?include_inactive=1' : ''}`),
  create: (uhid: string, data: Record<string, unknown>)     => req('POST',   `/patients/${uhid}/allergies`, data),
  update: (uhid: string, id: number, data: Record<string, unknown>) => req('PATCH',  `/patients/${uhid}/allergies/${id}`, data),
  remove: (uhid: string, id: number)       => req('DELETE', `/patients/${uhid}/allergies/${id}`),
};

// ── Encounter diagnoses ──────────────────────────────────────────
// What was diagnosed AT one visit, as opposed to patientDiagnoses above which
// is the longitudinal problem list. visitId is the visit CODE
// (VIS-<BRANCH>-YYYYMMDD-NNNN), matching the other /visits routes.
export const encounterDiagnoses = {
  list:    (visitId: string)          => req('GET',    `/visits/${visitId}/diagnoses`),
  create:  (visitId: string, data: Record<string, unknown>) => req('POST', `/visits/${visitId}/diagnoses`, data),
  update:  (visitId: string, id: number, data: Record<string, unknown>) => req('PATCH', `/visits/${visitId}/diagnoses/${id}`, data),
  remove:  (visitId: string, id: number) => req('DELETE', `/visits/${visitId}/diagnoses/${id}`),
  forPatient: (uhid: string)          => req('GET',    `/visits/patient/${uhid}/diagnoses`),
};

// ── Prescriptions ────────────────────────────────────────────────
// Header + items. save() replaces the whole item list in one transaction —
// the client is a form, so it sends the drug list it has rather than diffing
// it into per-item calls.
export const prescriptions = {
  list:       (params: QueryParams = {}) => req('GET', `/prescriptions?${qs(params)}`),
  get:        (id: number)          => req('GET', `/prescriptions/${id}`),
  forPatient: (uhid: string)        => req('GET', `/prescriptions/patient/${uhid}`),
  create:     (data: Record<string, unknown>) => req('POST', '/prescriptions', data),
  save:       (id: number, data: Record<string, unknown>) => req('PUT',  `/prescriptions/${id}`, data),
  setStatus:  (id: number, status: string, reason?: string) => req('PATCH', `/prescriptions/${id}/status`, { status, reason }),
};

// ── Patient documents ────────────────────────────────────────────
export const getPatientDocuments = (uhid: string)    => req('GET',  `/patients/${uhid}/documents`);
export const deletePatientDocument = (uhid: string, docId: number) => req('DELETE', `/patients/${uhid}/documents/${docId}`);

// Publishes (or un-publishes) a document into the patient app's own record
// (docs/HMS_V4_PATIENT_APP_DESIGN.md §17.2) — requires the
// 'records:release-to-patient' grant server-side. Reversible: passing
// 'staff' revokes a release made by mistake; both directions are audited.
export const releasePatientDocument = (uhid: string, docId: number, visibility: 'staff' | 'patient') =>
  req('PATCH', `/patients/${uhid}/documents/${docId}/release`, { visibility });

// Backs DocumentPreviewButtons.tsx's popup viewer — a document's plain-text
// content (already computed once, at generation/OCR time, into
// patient_documents.extracted_text — see that column's own comment) plus its
// OCR status, WITHOUT pulling the file's bytes at all. This is the whole
// reason "View" can show real content the way PreviousConsultations.tsx does
// for consultations: the text was already extracted and stored, so viewing
// it is a plain row read, not a file fetch + render.
export interface PatientDocumentStatus { id: number; ocr_status: string | null; extracted_text: string | null; file_type?: string | null }
export const getPatientDocumentStatus = (uhid: string, docId: number): Promise<PatientDocumentStatus> =>
  req('GET', `/patients/${uhid}/documents/${docId}/status`);
export async function uploadPatientDocument(uhid: string, file: File, category: string): Promise<any> {
  const form = new FormData();
  form.append('file', file);
  form.append('category', category);
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) { headers['Authorization'] = `Bearer ${token}`; Object.assign(headers, branchHeaders()); }
  const res = await fetch(`${BASE}/patients/${uhid}/documents`, { method: 'POST', headers, body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// Opens/downloads a document by fetching it (not a plain <a href> navigation).
// A raw navigation sends `Accept: text/html`, which CRA's dev-server proxy
// deliberately skips proxying (so it can serve the SPA shell for client-side
// routes instead) — the request never reaches the backend at all in `npm start`.
// Fetching it ourselves and opening the resulting blob sidesteps that entirely,
// and works the same way in a production build too.
export async function openPatientDocument(uhid: string, docId: number, opts: { inline?: boolean } = {}): Promise<void> {
  const token = getAuthToken();
  const res = await fetch(`${BASE}/patients/${uhid}/documents/${docId}/file${opts.inline ? '?disposition=inline' : ''}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') || '';
  const nameMatch = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename  = nameMatch?.[1] ? decodeURIComponent(nameMatch[1]) : `document-${docId}`;
  const blobUrl   = URL.createObjectURL(blob);

  if (opts.inline) {
    window.open(blobUrl, '_blank');
  } else {
    const a = document.createElement('a');
    a.href = blobUrl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}

// Fetches a patient document's bytes without deciding what to do with them —
// backs DocumentPreviewButtons.tsx's popup viewer, which needs the blob (and
// its content-type, to know whether it can actually render the file inline)
// rather than openPatientDocument's own open-a-new-tab/download behaviour.
// A separate function rather than a third `opts` flag on openPatientDocument,
// since that one's contract ("go do something with this file") is different
// in kind from "hand me the bytes so I can decide".
export async function fetchPatientDocumentBlob(uhid: string, docId: number): Promise<{ blob: Blob; contentType: string; filename: string }> {
  const token = getAuthToken();
  const res = await fetch(`${BASE}/patients/${uhid}/documents/${docId}/file?disposition=inline`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') || '';
  const nameMatch = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = nameMatch?.[1] ? decodeURIComponent(nameMatch[1]) : `document-${docId}`;
  const contentType = res.headers.get('content-type') || blob.type || '';
  return { blob, contentType, filename };
}

// ── Mobile scan-to-upload (QR) ──────────────────────────────────
// Staff side (authenticated, via req()): mint a session tied to this
// patient, and poll it while the QR modal is open.
// Passes this browser's own origin along — whatever address the staff member
// used to reach the app (a real domain, a LAN IP, localhost) is exactly what
// the backend should hand back in the QR, since a phone reaches "the app"
// the same way. No env var needed for the common case; see documents.js.
export const createMobileUploadSession = (uhid: string, category: string) => req('POST', `/patients/${uhid}/documents/mobile-session`, { category, origin: window.location.origin });
export const getMobileSessionStatus    = (uhid: string, token: string)    => req('GET',  `/patients/${uhid}/documents/mobile-session/${token}/status`);

// Phone side: hits the public /api/mobile-upload/:token routes directly —
// no auth token exists on a phone that scanned a QR code, so these bypass
// req()'s Authorization header injection entirely (the endpoints
// don't require or check them).
export async function getMobileUploadInfo(token: string): Promise<any> {
  const res = await fetch(`${BASE}/mobile-upload/${token}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
export async function submitMobileUpload(token: string, file: File, category: string): Promise<any> {
  const form = new FormData();
  form.append('file', file);
  form.append('category', category);
  const res = await fetch(`${BASE}/mobile-upload/${token}`, { method: 'POST', body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── Vendor invoice upload (Procurement) ──────────────────────────
// Same shape as patient-document upload above, just branch-scoped instead
// of patient-scoped — see routes/pharmacy-procurement.js's /vendor-invoices*
// endpoints and routes/mobile-upload-procurement.js for the phone side. Only
// the actual multipart upload lives here (this module is the one place that
// knows how to send a FormData body with the auth/branch headers attached,
// same convention as uploadPatientDocument above); the plain-JSON session/
// status endpoints are typed calls in features/pharmacy/api.ts instead.
export async function uploadVendorInvoice(file: File, poId?: number): Promise<any> {
  const form = new FormData();
  form.append('file', file);
  if (poId) form.append('po_id', String(poId));
  // The provider/model actually configured in Admin -> AI Settings for this
  // browser (src/aiConfig.ts) — every other AI feature in this app sends
  // this explicitly rather than letting the backend guess, and the vendor-
  // invoice extraction previously didn't, which is why it always tried
  // Anthropic even when a hospital had only configured an OpenAI key.
  const cfg = loadConfig();
  form.append('provider', cfg.provider);
  form.append('model', getActiveModel(cfg));
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) { headers['Authorization'] = `Bearer ${token}`; Object.assign(headers, branchHeaders()); }
  const res = await fetch(`${BASE}/pharmacy-procurement/vendor-invoices`, { method: 'POST', headers, body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// Phone side: hits the public /api/mobile-upload-procurement/:token routes
// directly — no auth token exists on a phone that scanned a QR code.
export async function getMobileUploadProcurementInfo(token: string): Promise<any> {
  const res = await fetch(`${BASE}/mobile-upload-procurement/${token}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
export async function submitMobileUploadProcurement(token: string, file: File): Promise<any> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE}/mobile-upload-procurement/${token}`, { method: 'POST', body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// Opens a bill's tax-invoice PDF the same fetch-then-blob way as patient
// documents (see openPatientDocument above), so the dev-server proxy and
// auth header both work correctly.
export async function openBillPdf(billId: number): Promise<void> {
  const token = getAuthToken();
  const res = await fetch(`${BASE}/billing/${billId}/pdf`, { headers: token ? { Authorization: `Bearer ${token}`, ...branchHeaders() } : {} });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}

// ── Drug Master (POC direct view/edit + Excel bulk upload) ───────
// See backend/routes/drug-master.js. Same fetch-then-blob-download
// convention as openBillPdf/openPatientDocument, and the same multipart
// upload convention as uploadVendorInvoice.
export async function downloadDrugMasterTemplate(): Promise<void> {
  const token = getAuthToken();
  const res = await fetch(`${BASE}/admin/drug-master/export`, { headers: token ? { Authorization: `Bearer ${token}`, ...branchHeaders() } : {} });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl; a.download = 'drug_master.xlsx';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}

export async function uploadDrugMasterExcel(file: File): Promise<any> {
  const form = new FormData();
  form.append('file', file);
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) { headers['Authorization'] = `Bearer ${token}`; Object.assign(headers, branchHeaders()); }
  const res = await fetch(`${BASE}/admin/drug-master/bulk-upload`, { method: 'POST', headers, body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── Hospital Formulary (Excel bulk upload) ───────────────────────
// See backend/routes/admin-formulary.js. Same download/upload conventions
// as Drug Master above.
export async function downloadFormularyTemplate(): Promise<void> {
  const token = getAuthToken();
  const res = await fetch(`${BASE}/admin/formulary/export`, { headers: token ? { Authorization: `Bearer ${token}`, ...branchHeaders() } : {} });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl; a.download = 'hospital_formulary.xlsx';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}

export async function uploadFormularyExcel(file: File): Promise<any> {
  const form = new FormData();
  form.append('file', file);
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) { headers['Authorization'] = `Bearer ${token}`; Object.assign(headers, branchHeaders()); }
  const res = await fetch(`${BASE}/admin/formulary/bulk-upload`, { method: 'POST', headers, body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── Visits ─────────────────────────────────────────────────────
export const getVisitsForPatient = (uhid: string)    => req('GET', `/visits/patient/${uhid}`);
export const searchVisits        = (q: string)       => req('GET', `/visits/search?q=${encodeURIComponent(q)}`);
// One visit with its stages and assigned doctor (GET /api/visits/:visitId).
export const getVisit = (visitId: string) => req('GET', `/visits/${visitId}`);
export const rollbackVisitStage = (visitId: string, reason: string) => req('PATCH', `/visits/${visitId}/rollback`, { reason });
// In-place correction of a completed prior stage (e.g. the doctor fixing a
// mistyped vitals reading from Consultation) — unlike rollback above, this
// never moves current_stage or disrupts the pipeline. See visits.js's
// AMENDABLE_STAGES for which stage/field combinations are accepted.
export const amendVisitStage = (visitId: string, stage: string, fields: Record<string, unknown>, reason: string) =>
  req('PATCH', `/visits/${visitId}/stage/${stage}/amend`, { fields, reason });

// ── Queue ──────────────────────────────────────────────────────
export const getTodayQueue   = (params: { patient_id?: number } = {}) => req('GET',  `/queue/today${params.patient_id ? `?patient_id=${params.patient_id}` : ''}`);
export const addToQueue      = (patient_id: number, extra: Record<string, unknown> = {}) => req('POST', '/queue/add', { patient_id, ...extra });
export const updateQueueStatus = (id: number, status: string) => req('PATCH', `/queue/${id}/status`, { status });
export const updateQueueAssignment = (id: number, data: Record<string, unknown>) => req('PATCH', `/queue/${id}`, data);
// Doctor consultation state — one active consultation per doctor, with an
// explicit emergency interruption. See backend/utils/consultationState.js and
// the typed equivalents in features/queue/api.ts (this legacy client is what
// the Visit Workflow's stage components still use).
export const getDoctorConsultationState = (doctorId: number) => req('GET', `/queue/doctor/${doctorId}/consultation-state`);
export const holdConsultation = (id: number, reason?: string) => req('POST', `/queue/${id}/hold`, reason ? { reason } : {});
export const resumeConsultation = (id: number) => req('POST', `/queue/${id}/resume`, {});
export const startEmergencyConsultation = (id: number) => req('POST', `/queue/${id}/emergency-consultation`, {});

// ── Branches (admin) ──────────────────────────────────────────────
export const getBranches      = ()                             => req('GET',  '/branches');
export const createBranch     = (data: Record<string, unknown>) => req('POST', '/branches', data);
export const updateBranch     = (id: number, data: Record<string, unknown>) => req('PATCH', `/branches/${id}`, data);

// ── Specialties (admin) ──────────────────────────────────────────
export const getSpecialties   = (all?: boolean)                => req('GET', `/specialties${all ? '?all=1' : ''}`);
export const createSpecialty  = (data: Record<string, unknown>) => req('POST', '/specialties', data);
export const updateSpecialty  = (id: number, data: Record<string, unknown>) => req('PATCH', `/specialties/${id}`, data);
export const deactivateSpecialty = (id: number)                => req('DELETE', `/specialties/${id}`);

// ── Doctor availability / queue settings (admin) ─────────────────
export const getQueueSettings   = ()                           => req('GET', '/doctor-availability/settings');
export const updateQueueSettings = (data: Record<string, unknown>) => req('PATCH', '/doctor-availability/settings', data);
export const getDoctorOnDutyStatus = ()                        => req('GET', '/doctor-availability/on-duty-status');
export const getDoctorAvailability = (params: QueryParams = {}) => req('GET', `/doctor-availability?${qs(params)}`);
export const getDoctorAvailabilityDay = (id: number)           => req('GET', `/doctor-availability/${id}`);
export const createDoctorAvailability = (data: Record<string, unknown>) => req('POST', '/doctor-availability', data);
export const updateDoctorAvailability = (id: number, data: Record<string, unknown>) => req('PATCH', `/doctor-availability/${id}`, data);
export const deleteDoctorAvailability = (id: number)           => req('DELETE', `/doctor-availability/${id}`);
export const addDoctorBreak     = (availabilityId: number, data: Record<string, unknown>) => req('POST', `/doctor-availability/${availabilityId}/breaks`, data);
export const deleteDoctorBreak  = (availabilityId: number, breakId: number) => req('DELETE', `/doctor-availability/${availabilityId}/breaks/${breakId}`);
export const getAvailabilityExceptions = (params: QueryParams = {}) => req('GET', `/doctor-availability/exceptions?${qs(params)}`);
export const reportUnavailability = (data: Record<string, unknown>) => req('POST', '/doctor-availability/exceptions', data);

// ── Appointment slots ─────────────────────────────────────────────
export const getAppointmentSlots = (params: QueryParams = {}) => req('GET', `/appointment-slots?${qs(params)}`);
export const getAppointmentSlot  = (id: number)                => req('GET', `/appointment-slots/${id}`);
export const bookAppointmentSlot = (id: number, data: Record<string, unknown>) => req('POST', `/appointment-slots/${id}/book`, data);
export const cancelAppointmentSlot = (id: number, reason?: string) => req('POST', `/appointment-slots/${id}/cancel`, { reason });
export const reassignAppointmentSlot = (id: number, newSlotId: number, reason?: string) => req('POST', `/appointment-slots/${id}/reassign`, { new_slot_id: newSlotId, reason });

// ── Kiosk booking (self-service, called by the kiosk with its device token) ──
export const kioskVerifyPatient = (name: string, mobile: string) => req('POST', '/kiosk-booking/verify', { name, mobile });
export const getKioskBookingDoctors = ()                       => req('GET', '/kiosk-booking/doctors');
export const getKioskBookingSlots = (doctorId: number, date?: string) => req('GET', `/kiosk-booking/slots?doctor_id=${doctorId}${date ? `&date=${date}` : ''}`);
export const kioskBookSlot = (patientId: number, slotId: number, intakeId?: number | null) => req('POST', '/kiosk-booking/book', { patient_id: patientId, slot_id: slotId, intake_id: intakeId ?? undefined });

// ── Public queue/appointment status page (no auth) ────────────────
export const getQueueStatus = (token: string): Promise<any> => fetch(`${BASE}/queue-status/${token}`).then(async r => {
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data;
});
// The link a patient is given — same origin, no auth, works for a booking's
// status_token (appointment_slots) or a checked-in queue entry's status_token.
export const queueStatusUrl = (token: string) => `${window.location.origin}/?queue_status=${token}`;

// ── Consultation ───────────────────────────────────────────────
export const saveConsultation = (data: Record<string, unknown>) => req('POST', '/consultation/save', data);
export const getConsultations = (patientId: number)  => req('GET',  `/consultation/patient/${patientId}`);
// G5: an approved note is corrected by a versioned amendment, never edited.
export const amendConsultation = (id: number, final_doc: string, amendment_reason: string) =>
  req('POST', `/consultation/${id}/amend`, { final_doc, amendment_reason });
export const getConsultationVersions = (id: number) => req('GET', `/consultation/${id}/versions`);
export const getConsultationMessages = (queueId: number) => req('GET', `/consultation/messages/${queueId}`);
export const addConsultationMessage  = (data: Record<string, unknown>) => req('POST', '/consultation/messages', data);
// Admission-scoped counterpart, for DischargeSummaryChat.tsx (an IP
// admission has no queue_id — see migration_discharge_summary_messages.sql).
export const getDischargeMessages = (admissionId: number) => req('GET', `/consultation/messages/admission/${admissionId}`);
// Proposes an approved Consultation Notes document as a de-identified
// example on the hospital's active AI Assistant Role profile — staged for
// Executive Director approval, same as an Admin's own manual edit there.
export const promoteConsultationToProfile = (consultationId: number) => req('POST', `/consultation/${consultationId}/promote-to-profile`);

// ── UI preferences (per-user, server-side) ───────────────────────
// A small generic store for display preferences (widths, toggles) that
// should follow the signed-in user across devices/browsers, not just
// persist in one browser's localStorage — see backend/routes/ui-preferences.js.
export const getUiPreferences = (): Promise<Record<string, unknown>> => req('GET', '/me/ui-preferences');
export const patchUiPreferences = (patch: Record<string, unknown>): Promise<Record<string, unknown>> => req('PATCH', '/me/ui-preferences', patch);

// ── AI ─────────────────────────────────────────────────────────
export const generateAIDoc   = (data: Record<string, unknown>) => req('POST', '/ai/generate-document', data);

// ── Billing ────────────────────────────────────────────────────
export const getAllBills        = ()                            => req('GET',  '/billing');
export const createOPDBill      = (data: Record<string, unknown>) => req('POST', '/billing/opd/create', data);
export const createPharmacyBill = (data: Record<string, unknown>) => req('POST', '/billing/pharmacy/create', data);
export const getOutstandingBills = (patientId: number)          => req('GET', `/billing/outstanding/${patientId}`);
export const getPatientBills    = (patientId: number)           => req('GET', `/billing/patient/${patientId}`);
export const markBillPaid       = (id: number)                  => req('PATCH', `/billing/${id}/mark-paid`);

// ── Pharmacy ───────────────────────────────────────────────────
// `channel` = only the stock that screen can sell (its store); omitted = every store.
export const getInventory          = (channel?: string)         => req('GET',  `/pharmacy/inventory${channel ? `?channel=${encodeURIComponent(channel)}` : ''}`);
export const getTodayPrescriptions = ()                         => req('GET',  '/pharmacy/prescriptions/today');
export const getStockAdjustments   = ()                             => req('GET',  '/pharmacy/stock-adjustments');
export const createStockAdjustment = (data: Record<string, unknown>) => req('POST', '/pharmacy/stock-adjustments', data);

// ── Pharmacy procurement (vendors / PO / GRN / schedule) ─────────
export const getVendors        = ()                             => req('GET',  '/pharmacy-procurement/vendors');
export const createVendor      = (data: Record<string, unknown>) => req('POST', '/pharmacy-procurement/vendors', data);
export const updateVendor      = (id: number, data: Record<string, unknown>) => req('PATCH', `/pharmacy-procurement/vendors/${id}`, data);
export const getPurchaseOrders = ()                             => req('GET',  '/pharmacy-procurement/purchase-orders');
export const createPurchaseOrder = (data: Record<string, unknown>) => req('POST', '/pharmacy-procurement/purchase-orders', data);
export const updatePOStatus    = (id: number, status: string)  => req('PATCH', `/pharmacy-procurement/purchase-orders/${id}/status`, { status });
export const getGoodsReceipts  = ()                             => req('GET',  '/pharmacy-procurement/goods-receipts');
export const createGoodsReceipt = (data: Record<string, unknown>) => req('POST', '/pharmacy-procurement/goods-receipts', data);
export const getScheduleRegister = ()                           => req('GET',  '/pharmacy-procurement/schedule-register');
export const updateMedicineSchedule = (id: number, schedule: string) => req('PATCH', `/pharmacy-procurement/medicines/${id}`, { schedule });

// ── Reports ────────────────────────────────────────────────────
export const getDailySummary = (window?: 'today' | '7d' | '30d') => req('GET', `/reports/daily-summary${window ? `?window=${window}` : ''}`);
export const getWaitTimes    = () => req('GET', '/reports/wait-times');
export const getPipelineStatus = (window?: 'today' | '7d' | '30d') => req('GET', `/reports/pipeline-status${window ? `?window=${window}` : ''}`);

// ── Intake sessions ──────────────────────────────────────────────
export const createIntakeSession = (data: Record<string, unknown>) => req('POST',  '/intake', data);
export const updateIntakeSession = (id: string | number, data: Record<string, unknown>) => req('PATCH', `/intake/${id}`, data);
export const getOpenIntakeSessions = ()          => req('GET',   '/intake/open');
export const deleteIntakeSession   = (id: string | number, reason?: string) => req('DELETE', `/intake/${id}`, { reason });

// Scan-to-upload against an intake with no patient yet (the kiosk's
// Documents step) — same session/status shape as createMobileUploadSession /
// getMobileSessionStatus above, so MobileUploadQRModal drives either one.
export const createIntakeUploadSession = (intakeId: number, category: string) => req('POST', `/intake/${intakeId}/upload-session`, { category, origin: window.location.origin });
export const getIntakeUploadSessionStatus = (intakeId: number, token: string) => req('GET', `/intake/${intakeId}/upload-session/${token}/status`);

export interface IntakeDocument {
  id: number;
  original_name: string;
  doc_category: string;
  file_type: string | null;
  file_size: number | null;
  uploaded_at: string;
  ocr_status: string | null;
  patient_document_id: number | null;
}
export const getIntakeDocuments = (intakeId: number): Promise<IntakeDocument[]> => req('GET', `/intake/${intakeId}/documents`);

// Fetched rather than navigated to, for the same dev-proxy/auth reasons as
// openPatientDocument above. inline → opens in a new tab (PDF/image render
// in the browser); otherwise downloads.
// The bytes themselves, for the in-page preview on the Patient intake screen
// (same role as fetchPatientDocumentBlob above).
export async function fetchIntakeDocumentBlob(intakeId: number, docId: number): Promise<{ blob: Blob; contentType: string; filename: string }> {
  const token = getAuthToken();
  const res = await fetch(`${BASE}/intake/${intakeId}/documents/${docId}/file?disposition=inline`, {
    headers: token ? { Authorization: `Bearer ${token}`, ...branchHeaders() } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') || '';
  const nameMatch = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = nameMatch?.[1] ? decodeURIComponent(nameMatch[1]) : `document-${docId}`;
  const contentType = res.headers.get('content-type') || blob.type || '';
  return { blob, contentType, filename };
}

export async function downloadIntakeDocument(intakeId: number, docId: number): Promise<void> {
  const { blob, filename } = await fetchIntakeDocumentBlob(intakeId, docId);
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}

// ── Kiosk device token ─────────────────────────────────────────
export const getKioskToken       = ()  => req('GET',  '/kiosk-auth/token');
export const regenerateKioskToken = () => req('POST', '/kiosk-auth/regenerate');
export const verifyKioskAccess    = ()  => req('GET',  '/kiosk-auth/verify');
export const getIntakeSession     = (id: string | number) => req('GET',   `/intake/${id}`);
